import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'

import {
    Bell,
    CheckCircle2,
    FileText,
    LogIn,
    PackageSearch,
    ShieldAlert,
    Smartphone,
    Users
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { LogoMark } from '@/components/generals/logo'

type QuoteStatus = 'Sent' | 'Accepted' | 'Draft'

const QUOTES: Array<{ number: string, customer: string, amount: string, status: QuoteStatus }> = [
    { number: 'QT-2041', customer: "Sharma Traders", amount: "₹1,24,500", status: 'Sent' },
    { number: 'QT-2040', customer: "Bright Homes", amount: "₹48,200", status: 'Accepted' },
    { number: 'QT-2039', customer: "Metro Interiors", amount: "₹2,10,000", status: 'Draft' }
]

const STATUS_STYLES: Record<QuoteStatus, string> = {
    Sent: 'bg-brand-50 text-brand-700 ring-brand-100',
    Accepted: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
    Draft: 'bg-slate-100 text-slate-600 ring-slate-200'
}

const SIGN_INS = [
    { initials: 'PS', name: "Priya S.", role: "Sales", time: "9:02 AM", tone: 'bg-brand-100 text-brand-700' },
    { initials: 'AM', name: "Arjun M.", role: "Warehouse", time: "9:14 AM", tone: 'bg-amber-100 text-amber-800' },
    { initials: 'NR', name: "Neha R.", role: "Accounts", time: "9:31 AM", tone: 'bg-emerald-100 text-emerald-800' }
]

const LOW_STOCK = [
    { item: "MCB 32A double pole", left: 6, level: 'w-[14%]', tone: 'bg-rose-500' },
    { item: "LED panel 18W", left: 14, level: 'w-[30%]', tone: 'bg-amber-500' },
    { item: "PVC conduit 25 mm", left: 22, level: 'w-[42%]', tone: 'bg-amber-400' }
]

const ALERTS = [
    { icon: ShieldAlert, title: "3 wrong PIN attempts", detail: "Counter tablet · 10:14 AM", tone: 'bg-rose-50 text-rose-600' },
    { icon: Smartphone, title: "Sign-in from a new device", detail: "Arjun M. · 9:14 AM", tone: 'bg-amber-50 text-amber-700' }
]

interface PanelProps {
    icon: LucideIcon
    title: string
    meta: ReactNode
    className?: string
    children: ReactNode
}

const Panel: FC<PanelProps> = ({ icon: Icon, title, meta, className, children }): ReactNode => {
    return (
        <div className={cn('rounded-2xl border border-border bg-white p-4 text-left shadow-[0_1px_2px_rgb(20_22_33/0.04)] sm:p-5', className)}>
            <div className='flex items-center justify-between gap-3'>
                <div className='flex items-center gap-2.5'>
                    <span className='grid size-8 place-items-center rounded-lg bg-brand-50 text-brand-600'>
                        <Icon className='size-4' />
                    </span>

                    <p className='text-sm font-semibold text-foreground'>
                        {title}
                    </p>
                </div>

                {meta}
            </div>

            <div className='mt-4'>
                {children}
            </div>
        </div>
    )
}

const Chip: FC<{ className?: string, children: ReactNode }> = ({ className, children }): ReactNode => {
    return (
        <span className={cn('rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1', className)}>
            {children}
        </span>
    )
}

const GlanceDashboard: FC = (): ReactNode => {
    return (
        <div className='relative'>
            <div
                aria-hidden='true'
                className='absolute -inset-x-10 -bottom-12 top-16 -z-10 rounded-[3rem] bg-[radial-gradient(closest-side,rgb(53_102_219/0.28),transparent)] blur-2xl'
            />

            <div
                role='img'
                aria-label="Preview of the Elenix Today screen, showing today's quotations, who signed in, low stock and security alerts"
                className='rounded-[1.75rem] border border-white bg-white/60 p-2 shadow-[0_40px_90px_-40px_rgb(19_31_69/0.45)] ring-1 ring-black/5 backdrop-blur sm:p-2.5'
            >
                <div className='overflow-hidden rounded-[1.35rem] border border-border bg-background'>
                    <div className='flex items-center justify-between gap-4 border-b border-border bg-white px-4 py-3 sm:px-5'>
                        <div className='flex items-center gap-3'>
                            <LogoMark className='size-8 rounded-lg text-sm' />

                            <div className='text-left'>
                                <p className='text-sm font-semibold leading-tight text-foreground'>
                                    Today
                                </p>

                                <p className='text-xs text-muted-foreground'>
                                    Updated just now
                                </p>
                            </div>
                        </div>

                        <div className='flex items-center gap-2'>
                            <span className='hidden items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-100 sm:inline-flex'>
                                <span className='size-1.5 rounded-full bg-emerald-500' />
                                Live
                            </span>

                            <span className='relative grid size-8 place-items-center rounded-full border border-border bg-white text-muted-foreground'>
                                <Bell className='size-4' />
                                <span className='absolute right-1.5 top-1.5 size-2 rounded-full bg-rose-500 ring-2 ring-white' />
                            </span>

                            <span className='grid size-8 place-items-center rounded-full bg-brand-900 text-xs font-semibold text-white'>
                                RK
                            </span>
                        </div>
                    </div>

                    <div className='p-3 sm:p-5'>
                        <div className='mb-4 flex flex-wrap items-end justify-between gap-3 px-1 text-left'>
                            <div>
                                <p className='text-xs font-medium text-muted-foreground'>
                                    Good morning, Rakesh
                                </p>

                                <p className='font-heading text-lg font-bold tracking-tight text-foreground sm:text-xl'>
                                    Today at a glance
                                </p>
                            </div>

                            <div className='hidden gap-2 md:flex'>
                                <Chip className='bg-white text-foreground ring-border'>5 quotes</Chip>
                                <Chip className='bg-white text-foreground ring-border'>8 signed in</Chip>
                                <Chip className='bg-amber-50 text-amber-800 ring-amber-100'>3 low stock</Chip>
                                <Chip className='bg-rose-50 text-rose-700 ring-rose-100'>2 alerts</Chip>
                            </div>
                        </div>

                        <div className='grid gap-3 sm:grid-cols-2 sm:gap-4'>
                            <Panel
                                icon={FileText}
                                title="Quotations"
                                meta={<Chip className='bg-white text-muted-foreground ring-border'>5 today</Chip>}
                            >
                                <div className='flex items-baseline justify-between gap-3'>
                                    <p className='font-heading text-2xl font-bold tracking-tight text-foreground tabular-nums'>
                                        ₹5,31,750
                                    </p>

                                    <p className='text-xs text-muted-foreground'>
                                        quoted today
                                    </p>
                                </div>

                                <ul className='mt-2 divide-y divide-border'>
                                    {QUOTES.map(quote => (
                                        <li key={quote.number} className='flex items-center justify-between gap-3 py-2.5'>
                                            <div className='min-w-0'>
                                                <p className='truncate text-sm font-medium text-foreground'>
                                                    {quote.customer}
                                                </p>

                                                <p className='font-mono text-[11px] text-muted-foreground'>
                                                    {quote.number}
                                                </p>
                                            </div>

                                            <div className='flex shrink-0 items-center gap-2'>
                                                <p className='text-sm font-semibold text-foreground tabular-nums'>
                                                    {quote.amount}
                                                </p>

                                                <Chip className={STATUS_STYLES[quote.status]}>
                                                    {quote.status}
                                                </Chip>
                                            </div>
                                        </li>
                                    ))}
                                </ul>

                                <p className='mt-1 text-xs font-medium text-brand-600'>
                                    +2 more today
                                </p>
                            </Panel>

                            <Panel
                                icon={Users}
                                title="Who signed in"
                                meta={<Chip className='bg-white text-muted-foreground ring-border'>8 of 11</Chip>}
                            >
                                <div className='h-2 overflow-hidden rounded-full bg-muted'>
                                    <div className='h-full w-[73%] rounded-full bg-primary' />
                                </div>

                                <ul className='mt-3 space-y-2.5'>
                                    {SIGN_INS.map(person => (
                                        <li key={person.name} className='flex items-center justify-between gap-3'>
                                            <div className='flex min-w-0 items-center gap-2.5'>
                                                <span className={cn('grid size-8 shrink-0 place-items-center rounded-full text-[11px] font-bold', person.tone)}>
                                                    {person.initials}
                                                </span>

                                                <div className='min-w-0'>
                                                    <p className='truncate text-sm font-medium text-foreground'>
                                                        {person.name}
                                                    </p>

                                                    <p className='text-[11px] text-muted-foreground'>
                                                        {person.role}
                                                    </p>
                                                </div>
                                            </div>

                                            <span className='inline-flex shrink-0 items-center gap-1 text-xs font-medium text-muted-foreground tabular-nums'>
                                                <LogIn className='size-3.5' />
                                                {person.time}
                                            </span>
                                        </li>
                                    ))}
                                </ul>

                                <p className='mt-3 text-xs font-medium text-brand-600'>
                                    +5 more signed in
                                </p>
                            </Panel>

                            <Panel
                                icon={PackageSearch}
                                title="Low stock"
                                className='hidden sm:block'
                                meta={<Chip className='bg-amber-50 text-amber-800 ring-amber-100'>3 items</Chip>}
                            >
                                <ul className='space-y-3.5'>
                                    {LOW_STOCK.map(stock => (
                                        <li key={stock.item}>
                                            <div className='flex items-center justify-between gap-3 text-sm'>
                                                <p className='truncate font-medium text-foreground'>
                                                    {stock.item}
                                                </p>

                                                <p className='shrink-0 text-xs font-semibold text-muted-foreground tabular-nums'>
                                                    {stock.left} left
                                                </p>
                                            </div>

                                            <div className='mt-1.5 h-1.5 overflow-hidden rounded-full bg-muted'>
                                                <div className={cn('h-full rounded-full', stock.level, stock.tone)} />
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </Panel>

                            <Panel
                                icon={ShieldAlert}
                                title="Security alerts"
                                className='hidden sm:block'
                                meta={<Chip className='bg-rose-50 text-rose-700 ring-rose-100'>2 new</Chip>}
                            >
                                <ul className='space-y-2.5'>
                                    {ALERTS.map(alert => (
                                        <li key={alert.title} className='flex items-start gap-3 rounded-xl border border-border bg-background p-3'>
                                            <span className={cn('grid size-8 shrink-0 place-items-center rounded-lg', alert.tone)}>
                                                <alert.icon className='size-4' />
                                            </span>

                                            <div className='min-w-0'>
                                                <p className='text-sm font-semibold text-foreground'>
                                                    {alert.title}
                                                </p>

                                                <p className='text-xs text-muted-foreground'>
                                                    {alert.detail}
                                                </p>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </Panel>
                        </div>
                    </div>
                </div>
            </div>

            <div
                aria-hidden='true'
                className='absolute -left-12 bottom-24 hidden w-64 xl:block motion-safe:animate-[float-soft_6s_ease-in-out_infinite]'
            >
                <div className='flex items-center gap-3 rounded-2xl border border-border bg-white p-3.5 text-left shadow-[0_20px_40px_-20px_rgb(19_31_69/0.35)]'>
                    <span className='grid size-9 shrink-0 place-items-center rounded-xl bg-emerald-50 text-emerald-600'>
                        <CheckCircle2 className='size-5' />
                    </span>

                    <div>
                        <p className='text-sm font-semibold text-foreground'>
                            QT-2040 accepted
                        </p>

                        <p className='text-xs text-muted-foreground'>
                            Bright Homes · ₹48,200
                        </p>
                    </div>
                </div>
            </div>

            <div
                aria-hidden='true'
                className='absolute -right-12 top-28 hidden w-60 xl:block motion-safe:animate-[float-soft_7s_ease-in-out_infinite] motion-safe:[animation-delay:1.5s]'
            >
                <div className='flex items-center gap-3 rounded-2xl border border-border bg-white p-3.5 text-left shadow-[0_20px_40px_-20px_rgb(19_31_69/0.35)]'>
                    <span className='grid size-9 shrink-0 place-items-center rounded-full bg-emerald-100 text-[11px] font-bold text-emerald-800'>
                        NR
                    </span>

                    <div>
                        <p className='text-sm font-semibold text-foreground'>
                            Neha R. signed in
                        </p>

                        <p className='text-xs text-muted-foreground'>
                            Accounts · 9:31 AM
                        </p>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default GlanceDashboard
